import {useMutation, useQuery, useQueryClient} from '@tanstack/react-query';
import {getQepSystem} from '@qep/shared/src';
import {debounce, DebouncedFunc} from 'lodash';
import {useCallback, useEffect, useRef} from 'react';

type Preferences = Record<string, unknown>;

export function usePreferences() {
  return useQuery({
    queryKey: ['preferences'],
    async queryFn() {
      try {
        const {data} = await getQepSystem().container.config.getPreference();
        return data;
      } catch (error) {
        getQepSystem().notification.local.error(error);
        throw error;
      }
    },
  });
}

export function selectPreference<T>(key: string) {
  const preferences = usePreferences();
  const preference = preferences.data ? (preferences.data[key] as T) : undefined;
  return {
    ...preferences,
    preference,
  };
}

export function usePreferenceInvalidator() {
  const queryClient = useQueryClient();
  return useCallback(async () => queryClient.invalidateQueries({queryKey: ['preferences']}), [queryClient]);
}

export function updatePreference<T>(key: string) {
  const queryClient = useQueryClient();
  const successNotificationRef = useRef<DebouncedFunc<(message: string) => void>>();

  useEffect(() => () => {
    successNotificationRef.current?.cancel();
    successNotificationRef.current = undefined;
  }, []);

  const notifySuccess = (message: string) => {
    successNotificationRef.current ??= debounce((successMessage: string) => {
      getQepSystem().notification.local.success(successMessage);
    }, 700);

    successNotificationRef.current(message);
  };

  return useMutation({
    async mutationFn(value: T) {
      return new Promise((resolve, reject) => {
        void getQepSystem().container.config.setPreference({field: key, value}, (error, result) => {
          if (error) {
            getQepSystem().notification.local.error(error);
            reject(error);
            return;
          }

          notifySuccess('Preference updated successfully');

          queryClient.setQueryData<Preferences>(['preferences'], _old => result!);
          resolve(result);
        });
      });
    },

    async onMutate(value: T) {
      await queryClient.cancelQueries({queryKey: ['preferences']});
      const previous = queryClient.getQueryData<Preferences>(['preferences']);
      queryClient.setQueryData<Preferences>(['preferences'], old => ({
        ...old,
        [key]: value,
      }));
      return {previous};
    },

    onError(error, _variables, context) {
      queryClient.setQueryData(['preferences'], context?.previous);
      getQepSystem().notification.local.error(error);
    },
  });
}
